import { chakra, defineRecipe } from '@plurall/elo-v3';

const bodyRecipe = defineRecipe({
  className: 'school-info-card__body',
  base: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
});

const headerRowRecipe = defineRecipe({
  className: 'school-info-card__header-row',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'gap.xsmall',
  },
});

const iconBoxRecipe = defineRecipe({
  className: 'school-info-card__icon-box',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 'size.56',
    height: 'size.56',
    borderRadius: 'border.radius.xsmall',
  },
});

const editGoalButtonRecipe = defineRecipe({
  className: 'school-info-card__edit-goal',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'gap.3xsmall',
    margin: 0,
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.semi.bold',
    color: 'foreground.brand',
  },
});

const schoolNameRecipe = defineRecipe({
  className: 'school-info-card__school-name',
  base: {
    margin: 0,
    marginTop: 'padding.small',
    fontSize: 'font.size.medium',
    fontWeight: 'font.weight.semi.bold',
    lineHeight: '150%',
    color: 'text.primary',
  },
});

const schoolLocationRecipe = defineRecipe({
  className: 'school-info-card__school-location',
  base: {
    margin: 0,
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.regular',
    lineHeight: '150%',
    color: 'text.tertiary',
  },
});

const sectionRecipe = defineRecipe({
  className: 'school-info-card__section',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.xsmall',
    marginTop: 'padding.medium',
  },
});

const progressBoxRecipe = defineRecipe({
  className: 'school-info-card__progress-box',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.xsmall',
    marginTop: 'padding.medium',
    padding: 'padding.small',
    borderRadius: 'border.radius.xsmall',
    backgroundColor: 'background.secondary',
  },
});

const detailRowRecipe = defineRecipe({
  className: 'school-info-card__detail-row',
  base: {
    display: 'grid',
    gridTemplateColumns: '96px minmax(0, 1fr)',
    gap: 'gap.xsmall',
    alignItems: 'center',
    width: '100%',
  },
});

const detailLabelRecipe = defineRecipe({
  className: 'school-info-card__detail-label',
  base: {
    margin: 0,
    fontSize: 'font.size.xsmall',
    fontWeight: 'font.weight.bold',
    lineHeight: '200%',
    letterSpacing: '0px',
    textTransform: 'uppercase',
    color: 'text.primary',
  },
});

const detailValueRecipe = defineRecipe({
  className: 'school-info-card__detail-value',
  base: {
    margin: 0,
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.regular',
    lineHeight: '150%',
    letterSpacing: '0%',
    color: 'text.tertiary',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
});

const progressHeaderRowRecipe = defineRecipe({
  className: 'school-info-card__progress-header-row',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'gap.xsmall',
  },
});

const progressLabelRecipe = defineRecipe({
  className: 'school-info-card__progress-label',
  base: {
    margin: 0,
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.semi.bold',
    color: 'text.secondary',
  },
});

const progressValueRecipe = defineRecipe({
  className: 'school-info-card__progress-value',
  base: {
    margin: 0,
    fontSize: 'font.size.small',
    fontWeight: 'font.weight.semi.bold',
    color: 'text.secondary',
  },
});

const progressFootnoteRecipe = defineRecipe({
  className: 'school-info-card__progress-footnote',
  base: {
    margin: 0,
    marginTop: 'padding.3xsmall',
    fontSize: 'font.size.xsmall',
    fontWeight: 'font.weight.regular',
    color: 'text.tertiary',
  },
});

const scoreListRecipe = defineRecipe({
  className: 'school-info-card__score-list',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.xsmall',
    width: '100%',
  },
});

const scoreCardRecipe = defineRecipe({
  className: 'school-info-card__score-card',
  base: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 'gap.xsmall',
    paddingBlock: 'padding.small',
    paddingInlineStart: 'padding.small',
    paddingInlineEnd: 0,
    borderRadius: 'border.radius.xsmall',
    minWidth: 0,
  },
});

const scoreIconBoxRecipe = defineRecipe({
  className: 'school-info-card__score-icon-box',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 'size.40',
    height: 'size.40',
    borderRadius: 'border.radius.xsmall',
    backgroundColor: 'background.primary',
  },
});

const scoreTextRecipe = defineRecipe({
  className: 'school-info-card__score-text',
  base: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
});

const scoreLabelRecipe = defineRecipe({
  className: 'school-info-card__score-label',
  base: {
    margin: 0,
    fontSize: 'font.size.xsmall',
    fontWeight: 'font.weight.regular',
    color: 'text.secondary',
    overflowWrap: 'anywhere',
  },
});

const scoreValueRecipe = defineRecipe({
  className: 'school-info-card__score-value',
  base: {
    margin: 0,
    fontSize: 'font.size.large',
    fontWeight: 'font.weight.bold',
    lineHeight: '125%',
  },
});

const emptyStateRecipe = defineRecipe({
  className: 'school-info-card__empty-state',
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'gap.xsmall',
    padding: 'padding.4xlarge',
    minHeight: '200px',
    height: '100%',
    textAlign: 'center',
  },
});

export const SchoolInfoCardBodyFactory = chakra('div', bodyRecipe);
export const SchoolInfoCardHeaderRowFactory = chakra('div', headerRowRecipe);
export const SchoolInfoCardIconBoxFactory = chakra('div', iconBoxRecipe);
export const SchoolInfoCardEditGoalButtonFactory = chakra('button', editGoalButtonRecipe);
export const SchoolInfoCardSchoolNameFactory = chakra('h3', schoolNameRecipe);
export const SchoolInfoCardSchoolLocationFactory = chakra('p', schoolLocationRecipe);
export const SchoolInfoCardSectionFactory = chakra('div', sectionRecipe);
export const SchoolInfoCardProgressBoxFactory = chakra('div', progressBoxRecipe);
export const SchoolInfoCardDetailRowFactory = chakra('div', detailRowRecipe);
export const SchoolInfoCardDetailLabelFactory = chakra('p', detailLabelRecipe);
export const SchoolInfoCardDetailValueFactory = chakra('p', detailValueRecipe);
export const SchoolInfoCardProgressHeaderRowFactory = chakra('div', progressHeaderRowRecipe);
export const SchoolInfoCardProgressLabelFactory = chakra('p', progressLabelRecipe);
export const SchoolInfoCardProgressValueFactory = chakra('p', progressValueRecipe);
export const SchoolInfoCardProgressFootnoteFactory = chakra('p', progressFootnoteRecipe);
export const SchoolInfoCardScoreListFactory = chakra('div', scoreListRecipe);
export const SchoolInfoCardScoreCardFactory = chakra('div', scoreCardRecipe);
export const SchoolInfoCardScoreIconBoxFactory = chakra('div', scoreIconBoxRecipe);
export const SchoolInfoCardScoreTextFactory = chakra('div', scoreTextRecipe);
export const SchoolInfoCardScoreLabelFactory = chakra('p', scoreLabelRecipe);
export const SchoolInfoCardScoreValueFactory = chakra('p', scoreValueRecipe);
export const SchoolInfoCardEmptyStateFactory = chakra('div', emptyStateRecipe);
